import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import '@/styles/editor.css'
import type { StaticPage, StaticPageFormData } from '@/types/static-page'
import React, { useEffect, useMemo, useState } from 'react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import Showdown from 'showdown'
import TurndownService from 'turndown'
import { gfm } from 'turndown-plugin-gfm'

interface StaticPageFormProps {
  page?: StaticPage | null
  onSubmit: (_data: StaticPageFormData) => void
  onCancel: () => void
  isLoading?: boolean
}

export const StaticPageForm: React.FC<StaticPageFormProps> = ({
  page,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState<StaticPageFormData>({
    title: '',
    slug: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    content: '',
    is_active: true,
    sort_order: 0,
  })

  // State for the WYSIWYG editor (HTML)
  const [editorContent, setEditorContent] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Initialize converters
  const turndownService = useMemo(() => {
    const service = new TurndownService({
      headingStyle: 'atx',
      codeBlockStyle: 'fenced',
    })
    service.use(gfm)
    return service
  }, [])

  const converter = useMemo(
    () =>
      new Showdown.Converter({
        tables: true,
        strikethrough: true,
        tasklists: true,
        openLinksInNewWindow: true,
      }),
    []
  )

  useEffect(() => {
    if (page) {
      setFormData({
        title: page.title || '',
        slug: page.slug || '',
        meta_title: page.meta_title || '',
        meta_description: page.meta_description || '',
        meta_keywords: page.meta_keywords || '',
        content: page.content || '',
        is_active: page.is_active ?? true,
        sort_order: page.sort_order ?? 0,
      })
      // Convert initial markdown to HTML for the editor
      setEditorContent(converter.makeHtml(page.content || ''))
    } else {
      setFormData({
        title: '',
        slug: '',
        meta_title: '',
        meta_description: '',
        meta_keywords: '',
        content: '',
        is_active: true,
        sort_order: 0,
      })
      setEditorContent('')
    }
  }, [page, converter])

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
  }

  const handleTitleChange = (value: string) => {
    setFormData((prev) => ({
      ...prev,
      title: value,
      // Auto-generate slug only if it's a new page or slug is empty
      slug: !page ? generateSlug(value) : prev.slug,
    }))
  }

  const handleChange = (field: keyof StaticPageFormData, value: string | boolean | number) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
    // Clear error when field is changed
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev }
        delete newErrors[field]
        return newErrors
      })
    }
  }

  const checkContentEmpty = (html: string) => {
    const doc = new DOMParser().parseFromString(html, 'text/html')
    return !doc.body.textContent?.trim()
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required'
    }

    if (checkContentEmpty(editorContent)) {
      newErrors.content = 'Content is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    // Convert HTML back to markdown for saving
    const markdownContent = turndownService.turndown(editorContent)

    const data: StaticPageFormData = {
      ...formData,
      content: markdownContent,
      slug: formData.slug || generateSlug(formData.title),
    }

    onSubmit(data)
  }

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link', 'clean'],
    ],
  }

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle>{page ? 'Edit Static Page' : 'Add Static Page'}</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">
                Title <span className="text-red-600">*</span>
              </Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => handleTitleChange(e.target.value)}
                placeholder="Enter page title"
                className={errors.title ? 'border-red-500' : ''}
              />
              {errors.title && <p className="text-xs text-red-500">{errors.title}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="slug">Slug</Label>
              <Input
                id="slug"
                value={formData.slug}
                onChange={(e) => handleChange('slug', e.target.value)}
                placeholder="page-slug"
              />
              <p className="text-xs text-gray-500">URL: /p/{formData.slug || 'slug'}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="meta_title">Meta Title</Label>
              <Input
                id="meta_title"
                value={formData.meta_title}
                onChange={(e) => handleChange('meta_title', e.target.value)}
                placeholder="SEO title (optional)"
              />
              <p className="text-xs text-gray-500">Leave empty to use page title</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="meta_keywords">Meta Keywords</Label>
              <Input
                id="meta_keywords"
                value={formData.meta_keywords}
                onChange={(e) => handleChange('meta_keywords', e.target.value)}
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="meta_description">Meta Description</Label>
            <Textarea
              id="meta_description"
              value={formData.meta_description}
              onChange={(e) => handleChange('meta_description', e.target.value)}
              placeholder="SEO description for search engines"
              rows={2}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="content">
              Content <span className="text-red-600">*</span>
            </Label>
            <div className="h-[800px] pb-12">
              <ReactQuill
                theme="snow"
                value={editorContent}
                onChange={setEditorContent}
                modules={modules}
                className="h-full"
              />
            </div>
            {errors.content && <p className="text-xs text-red-500">{errors.content}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="sort_order">Sort Order</Label>
              <Input
                id="sort_order"
                type="number"
                value={formData.sort_order}
                onChange={(e) => handleChange('sort_order', parseInt(e.target.value) || 0)}
                min={0}
              />
              <p className="text-xs text-gray-500">Lower number = higher priority</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="is_active">Status</Label>
              <div className="flex items-center space-x-2 pt-2">
                <Switch
                  id="is_active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => handleChange('is_active', checked)}
                />
                <Label htmlFor="is_active" className="text-sm text-gray-600">
                  {formData.is_active ? 'Active - Page is visible' : 'Inactive - Page is hidden'}
                </Label>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onCancel} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading ? 'Saving...' : page ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
